import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface PortalHeroCTA {
  labelKey?: string;
  label?: ReactNode;
  to?: string;
  href?: string;
  external?: boolean;
  variant?: 'primary' | 'secondary';
  icon?: ReactNode;
  onClick?: () => void;
}

interface PortalHeroProps {
  badge?: ReactNode;
  title: ReactNode;
  highlight?: ReactNode;
  subtitle?: ReactNode;
  description?: ReactNode;
  primary?: PortalHeroCTA;
  secondary?: PortalHeroCTA;
  /** theme color token used for decorative orbs/gradient; e.g. 'emerald', 'blue', 'amber' */
  accent?: 'emerald' | 'blue' | 'indigo' | 'amber' | 'cyan' | 'red' | 'slate';
  className?: string;
  children?: ReactNode;
  compact?: boolean;
}

const ACCENT_MAP: Record<NonNullable<PortalHeroProps['accent']>, { from: string; orbA: string; orbB: string; dot: string }> = {
  emerald: { from: 'from-emerald-500/[0.06]', orbA: 'bg-emerald-500/10', orbB: 'bg-teal-500/10', dot: 'rgba(16,185,129,0.08)' },
  blue:    { from: 'from-blue-500/[0.06]',    orbA: 'bg-blue-500/10',    orbB: 'bg-indigo-500/10', dot: 'rgba(59,130,246,0.08)' },
  indigo:  { from: 'from-indigo-500/[0.06]',  orbA: 'bg-indigo-500/10',  orbB: 'bg-violet-500/10', dot: 'rgba(99,102,241,0.08)' },
  amber:   { from: 'from-amber-500/[0.06]',   orbA: 'bg-amber-500/10',   orbB: 'bg-orange-500/10', dot: 'rgba(245,158,11,0.08)' },
  cyan:    { from: 'from-cyan-500/[0.06]',    orbA: 'bg-cyan-500/10',    orbB: 'bg-sky-500/10',    dot: 'rgba(6,182,212,0.08)' },
  red:     { from: 'from-red-500/[0.06]',     orbA: 'bg-red-500/10',     orbB: 'bg-rose-500/10',   dot: 'rgba(239,68,68,0.08)' },
  slate:   { from: 'from-slate-500/[0.04]',   orbA: 'bg-slate-500/10',   orbB: 'bg-zinc-500/10',   dot: 'rgba(100,116,139,0.06)' },
};

function CTAButton({ cta }: { cta: PortalHeroCTA }) {
  const isPrimary = cta.variant !== 'secondary';
  const base = 'inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl text-sm font-semibold transition-all';
  const cls = isPrimary
    ? `${base} bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20`
    : `${base} bg-background text-foreground border border-border hover:border-foreground/40 hover:bg-accent`;

  const content = (
    <>
      {cta.label}
      {cta.icon}
    </>
  );

  if (cta.external && cta.href) {
    return (
      <a href={cta.href} target="_blank" rel="noopener noreferrer" className={cls} onClick={cta.onClick}>
        {content}
      </a>
    );
  }
  if (cta.href) {
    return <a href={cta.href} className={cls} onClick={cta.onClick}>{content}</a>;
  }
  if (cta.to) {
    return <Link to={cta.to} className={cls} onClick={cta.onClick}>{content}</Link>;
  }
  return <button type="button" className={cls} onClick={cta.onClick}>{content}</button>;
}

export default function PortalHero({
  badge,
  title,
  highlight,
  subtitle,
  description,
  primary,
  secondary,
  accent = 'emerald',
  className = '',
  children,
  compact = false,
}: PortalHeroProps) {
  const a = ACCENT_MAP[accent];
  const padY = compact ? 'pt-16 pb-10' : 'pt-24 pb-16';

  return (
    <section className={`relative ${padY} px-4 sm:px-6 lg:px-8 overflow-hidden ${className}`}>
      {/* Gradient wash */}
      <div className={`absolute inset-0 bg-gradient-to-b ${a.from} via-transparent to-transparent`} />
      {/* Dot pattern */}
      <div
        className="absolute inset-0 opacity-60 dark:opacity-80"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, ${a.dot} 1px, transparent 0)`,
          backgroundSize: '32px 32px',
        }}
      />
      {/* Orbs */}
      <div className={`absolute top-12 left-[8%] w-72 h-72 ${a.orbA} rounded-full blur-3xl animate-pulse`} />
      <div className={`absolute bottom-0 right-[6%] w-64 h-64 ${a.orbB} rounded-full blur-3xl animate-pulse`} style={{ animationDelay: '1s' }} />

      <div className="relative max-w-5xl mx-auto text-center">
        {badge && (
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 mb-6 rounded-full border border-border bg-card/60 backdrop-blur text-xs font-medium text-muted-foreground">
            {badge}
          </div>
        )}
        <h1 className={`${compact ? 'text-4xl md:text-5xl' : 'text-5xl md:text-6xl lg:text-7xl'} font-extrabold tracking-tighter text-foreground leading-[1.05]`}>
          {title}
          {highlight && (
            <>
              {' '}
              <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                {highlight}
              </span>
            </>
          )}
        </h1>
        {subtitle && (
          <p className="mt-5 text-lg md:text-xl text-muted-foreground font-medium">
            {subtitle}
          </p>
        )}
        {description && (
          <p className="mt-4 max-w-2xl mx-auto text-base md:text-lg text-muted-foreground leading-relaxed">
            {description}
          </p>
        )}
        {(primary || secondary) && (
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            {primary && <CTAButton cta={primary} />}
            {secondary && <CTAButton cta={{ variant: 'secondary', ...secondary }} />}
          </div>
        )}
        {children && <div className="mt-12">{children}</div>}
      </div>
    </section>
  );
}
